import Helpers from './Helpers.js';
import SharedConfig from '../1mind/Config.js';
import Launcher from '../1mind/Launcher.js';

/**
 * OneMind — the 1mind "Superhuman" side of the bar (front-end label:
 * "AI Advisor").
 *
 * The launcher plumbing is NOT here: deployment ids, URL building, script
 * injection and mount detection all live in src2/1mind/ and are shared with
 * the page-level window.OneMind API (src2/1mind/index.js). This module is
 * only the bar's lifecycle — when to load, and how the panel reflects it.
 *
 * Note the two bundles each get their own copy of the shared module's state
 * (Rollup inlines it per bundle), so the cross-bundle "don't open two
 * sessions" guard is the DOM check inside Launcher.inject, not a shared
 * variable.
 *
 * Why this looks like Book.js and not Chat.js
 * -------------------------------------------
 * 1mind's WIDGET deployment mounts its own position:fixed element in the
 * bottom-right corner — the exact corner the bar occupies, already
 * contested by Intercom — and gives us no close API, which is what makes
 * Chat.js expensive (MutationObserver + suppression). The EMBEDDED
 * deployment mounts inside an element we own, so the bar's panel chrome
 * works exactly as it does for Book.
 *
 * Verified 2026-08-28 on thryv.test: when a #onemind-widget element exists,
 * the launcher mounts into it and renders as an embed EVEN with a widget
 * deployment id and data-display_mode="widget" set. The mount element wins,
 * so creating it inside the panel is what pins the embed in place.
 *
 * Load strategy: LAZY, on first AI Advisor click (Book's pattern), not eager
 * at init (Chat's pattern). The launcher is ~64KB and opens a 1mind session
 * as soon as it runs — eager-loading would open a session for every visitor
 * on every bar page, including the ones who never click.
 */

var OneMind = new Object();

/**
 * Run-once guard for the panel's own load — the launcher is injected once
 * per page and the mounted iframe stays mounted, so re-opening the panel
 * resumes the same conversation.
 */
OneMind.loaded = false;

/**
 * The panel element the mount is created inside.
 */
OneMind.container = '.thryv-connect-bar__panel--1mind .thryv-connect-bar__1mind-frame';

/**
 * Open the panel and, on first open only, load the launcher.
 *
 * Ordering matters twice here. The container lives inside the panel's
 * `<template x-if="oneMindLoaded">` (1mind-panel.php) so nothing
 * 1mind-shaped exists in the DOM until the visitor asks for it — flipping
 * oneMindLoaded is what creates it. Alpine renders that on the next tick,
 * and the element needs a layout pass after ($nextTick → rAF) because 1mind
 * sizes the iframe to its mount: measuring it mid-render yields a
 * zero-height embed.
 *
 * @param {object} component  Alpine component
 */
OneMind.open = function (component) {

  var _this = this;

  component.oneMindOpen = true;

  if (_this.loaded) return; // already mounted — panel just re-opens

  component.oneMindLoaded = true; // renders the container (x-if)

  var mount = function () {

    requestAnimationFrame(function () {

      _this.load(component);

    });

  };

  if (typeof component.$nextTick === 'function') {

    component.$nextTick(mount);

    return;

  }

  mount();

};

/**
 * Create the mount inside the panel and inject the launcher.
 *
 * @param {object} component  Alpine component
 */
OneMind.load = function (component) {

  var _this = this;

  if (_this.loaded) return;

  var settings = component.oneMind || {};

  if (!Launcher.mount(_this.container)) {

    component.oneMindState = 'unavailable';

    Helpers.log(component, 'OneMind.load — panel container missing, nothing to mount into');

    return;

  }

  _this.loaded = true;

  component.oneMindState = 'loading';

  try {

    Launcher.inject({
      useCase: settings.useCase || 'embedded',
      env: settings.env || SharedConfig.env,
      attributes: settings.attributes,
    });

  } catch (error) {

    // Unknown use case / bad env — Launcher throws rather than silently
    // loading the wrong deployment.
    component.oneMindState = 'unavailable';

    Helpers.log(component, 'OneMind.load — launcher not configured', error);

    return;

  }

  Launcher.whenReady(

    function () {

      component.oneMindState = 'ready';

      Helpers.log(component, 'OneMind.load — Superhuman mounted');

    },

    function () {

      component.oneMindState = 'unavailable';

      Helpers.log(component, 'OneMind.load — never mounted (deployment unconfigured?)');

    }

  );

};

/**
 * Close the panel. The iframe stays mounted so the conversation survives —
 * closing and re-opening resumes rather than restarting, the same way the
 * Book panel keeps a picked-but-unbooked slot.
 *
 * @param {object} component  Alpine component
 */
OneMind.hide = function (component) {

  component.oneMindOpen = false;

};

export default OneMind;
